import React from 'react';
import {StyleSheet, View} from 'react-native';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import {Shop} from '../../models/middleware';
import {storesContext} from '../../stores/use-stores';

import GiftCardPromotionExtraContent, {
  GiftCardPromotionExtraContentView,
} from './GiftCardPromotionExtraContent';

const noopNavigation = {
  navigate: () => undefined,
} as unknown as React.ComponentProps<typeof GiftCardPromotionExtraContent>['navigation'];

const meta = {
  title: 'Components/Gift Cards/Promotion Extra Content',
  component: GiftCardPromotionExtraContent,
  decorators: [
    Story => (
      <View style={styles.frame}>
        <Story />
      </View>
    ),
  ],
} satisfies Meta<typeof GiftCardPromotionExtraContent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {
  args: {
    navigation: noopNavigation,
    isRootStoreSelectionFlow: false,
  },
};

export const RootStoreSelectionFlow: Story = {
  args: {
    navigation: noopNavigation,
    isRootStoreSelectionFlow: true,
  },
};

// ---------------------------------------------------------------------------
// WithMockedStores — static data injected into the real View component.
// Renders the actual GiftCardPromotionExtraContentView with hardcoded
// shopList + availability="success". The embedded SuburbAutocomplete still
// mounts but receives a mock store via storesContext.Provider so it does
// nothing meaningful — every visible value below is hard-coded.
// ---------------------------------------------------------------------------

const MOCK_STORES = [
  {
    store_number: '001',
    name: 'Pineapple Hotel',
    distance: 1.0,
    delivery_available: false,
    pickup_available: true,
    instoreGiftCards: 'Live',
    address: {
      lines: ['706 Main Street'],
      locality: 'Kangaroo Point',
      administrative_area: 'qld',
      postal_code: '4169',
    },
  },
  {
    store_number: '002',
    name: 'MT Gravatt Cellars',
    distance: 8.1,
    delivery_available: true,
    pickup_available: true,
    instoreGiftCards: undefined,
    address: {
      lines: ['1226 Logan Road'],
      locality: 'Holland Park',
      administrative_area: 'qld',
      postal_code: '4121',
    },
  },
  {
    store_number: '003',
    name: 'Boondall Cellars',
    distance: 13.4,
    delivery_available: true,
    pickup_available: true,
    instoreGiftCards: 'Live',
    address: {
      lines: ['Cnr Beams And Sandgate Rds'],
      locality: 'Boondall',
      administrative_area: 'qld',
      postal_code: '4034',
    },
  },
] as unknown as Shop[];

// Minimal stub root store — only what SuburbAutocomplete needs to mount.
// Returns empty results / no-ops; the displayed data comes from mock props.
const MOCK_ROOT_STORE = {
  shopSelectionStore: {
    suburbList: [],
    suburbListLoading: false,
    geocoding: false,
    clearSuburbList: () => undefined,
    loadSuburbList: async () => undefined,
    reverseGeocode: async () => [],
    resetLoading: () => undefined,
    querySuburbStores: async () => [],
  },
} as unknown as React.ContextType<typeof storesContext>;

const MockedScreen = () => (
  <storesContext.Provider value={MOCK_ROOT_STORE}>
    <GiftCardPromotionExtraContentView
      isLoading={false}
      shopList={MOCK_STORES}
      availability="success"
      onSelectSuburb={() => undefined}
      onTermsAndConditionsPress={() => undefined}
      onBuyGiftCardPress={() => undefined}
    />
  </storesContext.Provider>
);

export const WithMockedStores: Story = {
  name: 'With Mocked Stores (static)',
  render: () => <MockedScreen />,
};

export const WithMockedStoresEmpty: Story = {
  name: 'With Mocked Stores — no stores in area',
  render: () => (
    <storesContext.Provider value={MOCK_ROOT_STORE}>
      <GiftCardPromotionExtraContentView
        isLoading={false}
        shopList={[]}
        availability="no-stores-in-area"
        bannerVisible={true}
        onSelectSuburb={() => undefined}
        onTermsAndConditionsPress={() => undefined}
        onBuyGiftCardPress={() => undefined}
      />
    </storesContext.Provider>
  ),
};

export const WithMockedStoresNoGiftCards: Story = {
  name: 'With Mocked Stores — no gift card stores',
  render: () => (
    <storesContext.Provider value={MOCK_ROOT_STORE}>
      <GiftCardPromotionExtraContentView
        isLoading={false}
        shopList={[]}
        availability="no-gift-card-stores"
        bannerVisible={true}
        onSelectSuburb={() => undefined}
        onTermsAndConditionsPress={() => undefined}
        onBuyGiftCardPress={() => undefined}
      />
    </storesContext.Provider>
  ),
};

const styles = StyleSheet.create({
  // Frame fills the storybook content area — no maxWidth so the rendered
  // story matches the actual viewport width (so designers see the same
  // layout the real app would produce). Subtle vertical padding only.
  frame: {
    backgroundColor: '#F3F4F6',
    paddingVertical: 16,
    paddingHorizontal: 16,
    minHeight: '100%',
    width: '100%',
  },
});
